import { supabase } from '@/lib/supabase';
import { buildScheduleTemplateCsv, downloadCsvFile } from '@/lib/scheduleTemplateCsv';
import { validateScheduleLinkMutation } from '@/lib/scheduleDependencyEngine';
import type { ScheduleLink, ScheduleTemplate, ScheduleTemplateItem, ScheduleTemplateLink, ScheduleLinkType } from '@/lib/scheduleUtils';

export const SCHEDULE_TEMPLATE_PROJECT_TYPES = ['Development', 'Renovation', 'Other'] as const;
export type ScheduleTemplateProjectType = (typeof SCHEDULE_TEMPLATE_PROJECT_TYPES)[number];

export const LIGHT_RENOVATION_TEMPLATE_KEY = '__light_renovation__';
export const BLANK_SCHEDULE_TEMPLATE_KEY = '__blank__';

export interface ScheduleTemplateListRow extends ScheduleTemplate {
  item_count: number;
  link_count: number;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

/** Draft = no schedule tasks yet (computed; no DB status column). */
export function isScheduleTemplateDraft(template: { item_count?: number }): boolean {
  return (template.item_count ?? 0) === 0;
}

export function isEmptyScheduleTemplate(template: {
  item_count?: number;
  link_count?: number;
}): boolean {
  return (template.item_count ?? 0) === 0 && (template.link_count ?? 0) === 0;
}

export function canSetScheduleTemplateAsDefault(template: { item_count?: number }): boolean {
  return (template.item_count ?? 0) > 0;
}

export interface ScheduleTemplateSection {
  id: string;
  name: string;
  sort_order: number;
  items: ScheduleTemplateItem[];
}

export interface ScheduleTemplateWithItems {
  template: ScheduleTemplateListRow | null;
  /** Always an array; never assume present on raw API payloads. */
  sections: ScheduleTemplateSection[];
  items: ScheduleTemplateItem[];
  links: ScheduleTemplateLink[];
  loadError: string | null;
}

/** Normalize optional section list from API or nested template payloads. */
export function normalizeTemplateSections(
  raw: unknown,
  templateId = ''
): ScheduleTemplateSection[] {
  if (!Array.isArray(raw)) return [];
  const sections: ScheduleTemplateSection[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const rec = entry as Record<string, unknown>;
    const sectionItems = Array.isArray(rec.items)
      ? rec.items.map((row) =>
          normalizeScheduleTemplateItem(row as Record<string, unknown>, templateId)
        )
      : [];
    sections.push({
      id:
        typeof rec.id === 'string' && rec.id
          ? rec.id
          : `section-${sections.length + 1}`,
      name:
        typeof rec.name === 'string' && rec.name.trim()
          ? rec.name.trim()
          : typeof rec.title === 'string' && rec.title.trim()
            ? rec.title.trim()
            : 'Section',
      sort_order:
        typeof rec.sort_order === 'number' && Number.isFinite(rec.sort_order)
          ? rec.sort_order
          : sections.length + 1,
      items: sectionItems,
    });
  }
  return sections;
}

export function normalizeScheduleTemplateItem(
  row: Record<string, unknown>,
  templateId = ''
): ScheduleTemplateItem {
  const sortOrder =
    typeof row.sort_order === 'number' && Number.isFinite(row.sort_order)
      ? row.sort_order
      : 0;
  const duration =
    typeof row.default_duration_days === 'number' && row.default_duration_days > 0
      ? row.default_duration_days
      : 1;

  return {
    id: typeof row.id === 'string' ? row.id : '',
    template_id:
      typeof row.template_id === 'string' ? row.template_id : templateId,
    sort_order: sortOrder,
    default_task_no:
      row.default_task_no != null && row.default_task_no !== ''
        ? Number(row.default_task_no)
        : null,
    title: typeof row.title === 'string' ? row.title : '',
    phase: typeof row.phase === 'string' ? row.phase : null,
    default_duration_days: Number.isFinite(duration) ? duration : 1,
    default_is_included: row.default_is_included !== false,
    default_assigned_role:
      typeof row.default_assigned_role === 'string' ? row.default_assigned_role : null,
    default_assigned_to:
      typeof row.default_assigned_to === 'string' ? row.default_assigned_to : null,
    notes: typeof row.notes === 'string' ? row.notes : null,
  };
}

function normalizeScheduleTemplateLink(row: Record<string, unknown>): ScheduleTemplateLink | null {
  const predecessor = row.predecessor_item_id;
  const successor = row.successor_item_id;
  if (typeof predecessor !== 'string' || typeof successor !== 'string') return null;
  const linkType = row.link_type;
  const allowed = ['FS', 'SS', 'FF', 'SF'] as const;
  const resolved = allowed.includes(linkType as (typeof allowed)[number])
    ? (linkType as ScheduleTemplateLink['link_type'])
    : 'FS';

  return {
    id: typeof row.id === 'string' ? row.id : '',
    template_id: typeof row.template_id === 'string' ? row.template_id : '',
    predecessor_item_id: predecessor,
    successor_item_id: successor,
    link_type: resolved,
    lag_days:
      typeof row.lag_days === 'number' && Number.isFinite(row.lag_days) ? row.lag_days : 0,
  };
}

function normalizeTemplateItems(raw: unknown, templateId: string): ScheduleTemplateItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) =>
    normalizeScheduleTemplateItem(
      row && typeof row === 'object' ? (row as Record<string, unknown>) : {},
      templateId
    )
  );
}

function normalizeTemplateLinks(raw: unknown): ScheduleTemplateLink[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) =>
      normalizeScheduleTemplateLink(
        row && typeof row === 'object' ? (row as Record<string, unknown>) : {}
      )
    )
    .filter((link): link is ScheduleTemplateLink => link != null);
}

function emptyTemplateDetail(loadError: string | null = null): ScheduleTemplateWithItems {
  return {
    template: null,
    sections: [],
    items: [],
    links: [],
    loadError,
  };
}

export interface ScheduleTemplatePickerOption {
  id: string;
  name: string;
  project_type: string;
  description: string | null;
  item_count: number;
  link_count: number;
  is_default: boolean;
  source: 'database';
}

type TemplateRow = Record<string, unknown>;

const TEMPLATE_SELECT =
  'id, name, project_type, description, is_active, is_default, created_at, updated_at';

function mapTemplateRow(row: TemplateRow, itemCount = 0, linkCount = 0): ScheduleTemplateListRow | null {
  const id = row.id;
  const name = row.name;
  if (typeof id !== 'string' || !id || typeof name !== 'string') {
    return null;
  }

  return {
    id,
    name: name.trim() || 'Untitled template',
    project_type: typeof row.project_type === 'string' ? row.project_type : 'Other',
    description: (row.description as string | null) ?? null,
    is_active: row.is_active !== false,
    is_default: row.is_default === true && itemCount > 0,
    created_at: (row.created_at as string) ?? new Date().toISOString(),
    updated_at: (row.updated_at as string) ?? new Date().toISOString(),
    item_count: Math.max(0, itemCount),
    link_count: Math.max(0, linkCount),
  };
}

async function countTemplateItems(templateId: string): Promise<{ items: number; links: number }> {
  try {
    const [{ count: itemCount }, { count: linkCount }] = await Promise.all([
      supabase
        .from('jphph_schedule_template_items')
        .select('id', { count: 'exact', head: true })
        .eq('template_id', templateId),
      supabase
        .from('jphph_schedule_template_links')
        .select('id', { count: 'exact', head: true })
        .eq('template_id', templateId),
    ]);
    return { items: itemCount ?? 0, links: linkCount ?? 0 };
  } catch (err) {
    console.warn('[scheduleTemplateManagement] count error:', err);
    return { items: 0, links: 0 };
  }
}

/** Empty templates must not remain default — they are not usable for new projects. */
async function syncTemplateDefaultStatus(templateId: string): Promise<void> {
  const counts = await countTemplateItems(templateId);
  if (counts.items === 0) {
    await supabase
      .from('jphph_schedule_templates')
      .update({ is_default: false, updated_at: new Date().toISOString() })
      .eq('id', templateId)
      .eq('is_default', true);
  }
}

export async function fetchAllScheduleTemplates(options?: {
  includeInactive?: boolean;
}): Promise<{ templates: ScheduleTemplateListRow[]; error: string | null }> {
  try {
    let query = supabase
      .from('jphph_schedule_templates')
      .select(TEMPLATE_SELECT)
      .order('project_type', { ascending: true })
      .order('is_default', { ascending: false })
      .order('name', { ascending: true });

    if (!options?.includeInactive) {
      query = query.eq('is_active', true);
    }

    let { data, error } = await query;

    if (error?.message?.includes('is_default')) {
      let fallbackQuery = supabase
        .from('jphph_schedule_templates')
        .select('id, name, project_type, description, is_active, created_at, updated_at')
        .order('project_type', { ascending: true })
        .order('name', { ascending: true });
      if (!options?.includeInactive) {
        fallbackQuery = fallbackQuery.eq('is_active', true);
      }
      const fallback = await fallbackQuery;
      data = fallback.data;
      error = fallback.error;
    }

    if (error) {
      console.warn('[scheduleTemplateManagement] list error:', error.message);
      return { templates: [], error: error.message };
    }

    const rows = Array.isArray(data) ? (data as TemplateRow[]) : [];
    for (const row of rows) {
      if (row.is_default === true) {
        const templateId = typeof row.id === 'string' ? row.id : '';
        if (templateId) await syncTemplateDefaultStatus(templateId);
      }
    }

    const templates: ScheduleTemplateListRow[] = [];
    for (const row of rows) {
      const templateId = typeof row.id === 'string' ? row.id : '';
      if (!templateId) continue;
      const counts = await countTemplateItems(templateId);
      const mapped = mapTemplateRow(row, counts.items, counts.links);
      if (mapped) templates.push(mapped);
    }
    return { templates, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to load schedule templates';
    console.warn('[scheduleTemplateManagement] list unexpected error:', message);
    return { templates: [], error: message };
  }
}

export async function fetchScheduleTemplateById(
  templateId: string
): Promise<ScheduleTemplateWithItems> {
  const { data: templateData, error: templateErr } = await supabase
    .from('jphph_schedule_templates')
    .select(TEMPLATE_SELECT)
    .eq('id', templateId)
    .maybeSingle();

  if (templateErr || !templateData) {
    return emptyTemplateDetail(templateErr?.message ?? 'Template not found');
  }

  const counts = await countTemplateItems(templateId);
  const mappedTemplate = mapTemplateRow(templateData as TemplateRow, counts.items, counts.links);
  if (!mappedTemplate) {
    return emptyTemplateDetail('Template not found');
  }

  const [{ data: items, error: itemsErr }, { data: links, error: linksErr }] = await Promise.all([
    supabase
      .from('jphph_schedule_template_items')
      .select('*')
      .eq('template_id', templateId)
      .order('sort_order', { ascending: true }),
    supabase.from('jphph_schedule_template_links').select('*').eq('template_id', templateId),
  ]);

  const normalizedItems = normalizeTemplateItems(items, templateId);
  const normalizedLinks = normalizeTemplateLinks(links);
  const rawPayload = templateData as TemplateRow;
  const sections = normalizeTemplateSections(rawPayload.sections, templateId);

  if (itemsErr) {
    console.warn('[scheduleTemplateManagement] items error:', itemsErr.message);
    return {
      template: mappedTemplate,
      sections,
      items: [],
      links: normalizedLinks,
      loadError: itemsErr.message,
    };
  }
  if (linksErr) {
    console.warn('[scheduleTemplateManagement] links error:', linksErr.message);
  }

  return {
    template: mappedTemplate,
    sections,
    items: normalizedItems,
    links: normalizedLinks,
    loadError: null,
  };
}

export async function fetchScheduleTemplatesForProjectType(
  projectType: string,
  fetchOptions?: { usableOnly?: boolean }
): Promise<ScheduleTemplatePickerOption[]> {
  try {
    let { data, error } = await supabase
      .from('jphph_schedule_templates')
      .select(TEMPLATE_SELECT)
      .eq('project_type', projectType)
      .eq('is_active', true)
      .order('is_default', { ascending: false })
      .order('name', { ascending: true });

    if (error?.message?.includes('is_default')) {
      const fallback = await supabase
        .from('jphph_schedule_templates')
        .select('id, name, project_type, description, is_active, created_at, updated_at')
        .eq('project_type', projectType)
        .eq('is_active', true)
        .order('name', { ascending: true });
      data = fallback.data;
      error = fallback.error;
    }

    if (error) {
      console.warn('[scheduleTemplateManagement] picker error:', error.message);
      return [];
    }

    const rows = Array.isArray(data) ? (data as TemplateRow[]) : [];
    const options: ScheduleTemplatePickerOption[] = [];
    for (const row of rows) {
      const templateId = typeof row.id === 'string' ? row.id : '';
      if (!templateId) continue;
      const counts = await countTemplateItems(templateId);
      const mapped = mapTemplateRow(row, counts.items, counts.links);
      if (!mapped) continue;
      options.push({
        id: mapped.id,
        name: mapped.name,
        project_type: mapped.project_type,
        description: mapped.description,
        item_count: mapped.item_count,
        link_count: mapped.link_count,
        is_default: mapped.is_default,
        source: 'database',
      });
    }
    const filtered = fetchOptions?.usableOnly
      ? options.filter((opt) => opt.item_count > 0)
      : options;
    return filtered;
  } catch (err) {
    console.warn('[scheduleTemplateManagement] picker unexpected error:', err);
    return [];
  }
}

export async function resolveDefaultTemplateId(projectType: string): Promise<string | null> {
  const { data: defaultRows, error: defaultErr } = await supabase
    .from('jphph_schedule_templates')
    .select('id')
    .eq('project_type', projectType)
    .eq('is_active', true)
    .eq('is_default', true);

  if (!defaultErr && Array.isArray(defaultRows)) {
    for (const row of defaultRows) {
      const id = typeof row.id === 'string' ? row.id : '';
      if (!id) continue;
      const counts = await countTemplateItems(id);
      if (counts.items > 0) return id;
    }
  }

  const { data: activeRows, error: activeErr } = await supabase
    .from('jphph_schedule_templates')
    .select('id')
    .eq('project_type', projectType)
    .eq('is_active', true)
    .order('created_at', { ascending: false });

  if (activeErr) {
    console.warn('[scheduleTemplateManagement] default resolve error:', activeErr.message);
    return null;
  }

  let bestId: string | null = null;
  let bestItems = 0;
  for (const row of activeRows ?? []) {
    const id = typeof row.id === 'string' ? row.id : '';
    if (!id) continue;
    const counts = await countTemplateItems(id);
    if (counts.items > bestItems) {
      bestItems = counts.items;
      bestId = id;
    }
  }

  return bestItems > 0 ? bestId : null;
}

export async function createScheduleTemplate(params: {
  name: string;
  project_type: string;
  description?: string | null;
}): Promise<{ template: ScheduleTemplateListRow | null; error: string | null }> {
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('jphph_schedule_templates')
    .insert({
      name: params.name.trim(),
      project_type: params.project_type,
      description: params.description?.trim() || null,
      is_active: true,
      is_default: false,
      created_at: now,
      updated_at: now,
    })
    .select(TEMPLATE_SELECT)
    .maybeSingle();

  if (error || !data) {
    return { template: null, error: error?.message ?? 'Failed to create template' };
  }
  const template = mapTemplateRow(data as TemplateRow, 0, 0);
  if (!template) {
    return { template: null, error: 'Failed to create template' };
  }
  return { template, error: null };
}

export async function updateScheduleTemplateMeta(
  templateId: string,
  patch: { name?: string; description?: string | null }
): Promise<{ error: string | null }> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.name != null) payload.name = patch.name.trim();
  if (patch.description !== undefined) payload.description = patch.description?.trim() || null;

  const { error } = await supabase
    .from('jphph_schedule_templates')
    .update(payload)
    .eq('id', templateId);

  if (error) return { error: error.message };
  return { error: null };
}

export async function setDefaultScheduleTemplate(
  templateId: string,
  projectType: string
): Promise<{ error: string | null }> {
  const counts = await countTemplateItems(templateId);
  if (counts.items === 0) {
    return { error: 'Add at least one task before setting this template as default.' };
  }

  const { error: clearErr } = await supabase
    .from('jphph_schedule_templates')
    .update({ is_default: false, updated_at: new Date().toISOString() })
    .eq('project_type', projectType)
    .eq('is_default', true);

  if (clearErr) return { error: clearErr.message };

  const { error } = await supabase
    .from('jphph_schedule_templates')
    .update({ is_default: true, updated_at: new Date().toISOString() })
    .eq('id', templateId);

  if (error) return { error: error.message };
  return { error: null };
}

export async function archiveScheduleTemplate(templateId: string): Promise<{ error: string | null }> {
  const { data: row } = await supabase
    .from('jphph_schedule_templates')
    .select('is_default, project_type')
    .eq('id', templateId)
    .maybeSingle();

  if (row?.is_default) {
    const projectType =
      typeof row.project_type === 'string' && row.project_type.trim()
        ? row.project_type
        : 'this project type';
    const counts = await countTemplateItems(templateId);
    if (counts.items > 0) {
      return {
        error: `This is the default ${projectType} template. Set another ${projectType} template as default before archiving.`,
      };
    }
  }

  const { error } = await supabase
    .from('jphph_schedule_templates')
    .update({ is_active: false, is_default: false, updated_at: new Date().toISOString() })
    .eq('id', templateId);

  if (error) return { error: error.message };
  return { error: null };
}

export async function deleteScheduleTemplate(
  templateId: string
): Promise<{ error: string | null }> {
  const { data: row } = await supabase
    .from('jphph_schedule_templates')
    .select('is_default, project_type, name')
    .eq('id', templateId)
    .maybeSingle();

  if (!row) return { error: 'Template not found' };

  const counts = await countTemplateItems(templateId);
  const isEmpty = counts.items === 0 && counts.links === 0;

  if (row.is_default && !isEmpty) {
    const projectType =
      typeof row.project_type === 'string' && row.project_type.trim()
        ? row.project_type
        : 'this project type';
    return {
      error: `This is the default ${projectType} template. Set another ${projectType} template as default before deleting.`,
    };
  }

  if (!isEmpty) {
    const { count } = await supabase
      .from('jphph_schedule_templates')
      .select('id', { count: 'exact', head: true })
      .eq('project_type', row.project_type as string)
      .eq('is_active', true);

    if ((count ?? 0) <= 1) {
      return {
        error: `Cannot delete the only active template for ${row.project_type}. Archive or create another template first.`,
      };
    }
  }

  await supabase.from('jphph_schedule_template_links').delete().eq('template_id', templateId);
  await supabase.from('jphph_schedule_template_items').delete().eq('template_id', templateId);

  const { error } = await supabase.from('jphph_schedule_templates').delete().eq('id', templateId);
  if (error) return { error: error.message };
  return { error: null };
}

export async function duplicateScheduleTemplate(
  sourceTemplateId: string,
  newName: string
): Promise<{ templateId: string | null; error: string | null }> {
  const source = await fetchScheduleTemplateById(sourceTemplateId);
  if (!source.template) {
    return { templateId: null, error: source.loadError ?? 'Source template not found' };
  }

  const now = new Date().toISOString();
  const { data: created, error: createErr } = await supabase
    .from('jphph_schedule_templates')
    .insert({
      name: newName.trim(),
      project_type: source.template.project_type,
      description: source.template.description,
      is_active: true,
      is_default: false,
      created_at: now,
      updated_at: now,
    })
    .select('id')
    .maybeSingle();

  if (createErr || !created?.id) {
    return { templateId: null, error: createErr?.message ?? 'Failed to duplicate template' };
  }

  const newTemplateId = created.id as string;
  const idMap = new Map<string, string>();

  const BATCH = 100;
  for (let i = 0; i < source.items.length; i += BATCH) {
    const chunk = source.items.slice(i, i + BATCH);
    const inserts = chunk.map((item) => ({
      template_id: newTemplateId,
      sort_order: item.sort_order,
      default_task_no: item.default_task_no,
      title: item.title,
      phase: item.phase,
      default_duration_days: item.default_duration_days,
      default_is_included: item.default_is_included !== false,
      default_assigned_role: item.default_assigned_role,
      default_assigned_to: item.default_assigned_to,
      notes: item.notes,
    }));

    const { data: inserted, error: insertErr } = await supabase
      .from('jphph_schedule_template_items')
      .insert(inserts)
      .select('id, sort_order');

    if (insertErr) {
      await supabase.from('jphph_schedule_templates').delete().eq('id', newTemplateId);
      return { templateId: null, error: insertErr.message };
    }

    (inserted ?? []).forEach((row) => {
      const oldItem = chunk.find((c) => c.sort_order === (row as TemplateRow).sort_order);
      if (oldItem?.id) idMap.set(oldItem.id, (row as TemplateRow).id as string);
    });
  }

  if (source.links.length > 0) {
    const linkInserts = source.links
      .map((link) => {
        const pred = idMap.get(link.predecessor_item_id);
        const succ = idMap.get(link.successor_item_id);
        if (!pred || !succ) return null;
        return {
          template_id: newTemplateId,
          predecessor_item_id: pred,
          successor_item_id: succ,
          link_type: link.link_type,
          lag_days: link.lag_days ?? 0,
        };
      })
      .filter(Boolean);

    if (linkInserts.length > 0) {
      const { error: linkErr } = await supabase
        .from('jphph_schedule_template_links')
        .insert(linkInserts);
      if (linkErr) {
        console.error('[scheduleTemplateManagement] duplicate links warning:', linkErr);
      }
    }
  }

  await supabase
    .from('jphph_schedule_templates')
    .update({ updated_at: new Date().toISOString() })
    .eq('id', newTemplateId);

  return { templateId: newTemplateId, error: null };
}

export type TemplateItemDraft = {
  id?: string;
  sort_order: number;
  default_task_no: number | null;
  title: string;
  phase: string | null;
  default_duration_days: number;
  default_is_included: boolean;
  default_assigned_role?: string | null;
  default_assigned_to?: string | null;
  notes?: string | null;
};

export async function saveScheduleTemplateItems(
  templateId: string,
  items: TemplateItemDraft[],
  removedItemIds: string[]
): Promise<{ error: string | null }> {
  const now = new Date().toISOString();

  if (removedItemIds.length > 0) {
    await supabase
      .from('jphph_schedule_template_links')
      .delete()
      .in('predecessor_item_id', removedItemIds);
    await supabase
      .from('jphph_schedule_template_links')
      .delete()
      .in('successor_item_id', removedItemIds);
    const { error: delErr } = await supabase
      .from('jphph_schedule_template_items')
      .delete()
      .in('id', removedItemIds);
    if (delErr) return { error: delErr.message };
  }

  const BATCH = 50;
  for (let i = 0; i < items.length; i += BATCH) {
    const chunk = items.slice(i, i + BATCH);
    for (const item of chunk) {
      const payload = {
        template_id: templateId,
        sort_order: item.sort_order,
        default_task_no: item.default_task_no,
        title: item.title.trim() || 'Untitled',
        phase: item.phase?.trim() || null,
        default_duration_days: item.default_duration_days || 1,
        default_is_included: item.default_is_included !== false,
        default_assigned_role: item.default_assigned_role ?? null,
        default_assigned_to: item.default_assigned_to ?? null,
        notes: item.notes?.trim() || null,
      };

      if (item.id) {
        const { error } = await supabase
          .from('jphph_schedule_template_items')
          .update(payload)
          .eq('id', item.id);
        if (error) return { error: error.message };
      } else {
        const { error } = await supabase.from('jphph_schedule_template_items').insert(payload);
        if (error) return { error: error.message };
      }
    }
  }

  await supabase
    .from('jphph_schedule_templates')
    .update({ updated_at: now })
    .eq('id', templateId);

  await syncTemplateDefaultStatus(templateId);

  return { error: null };
}

export type TemplateLinkDraft = {
  id?: string;
  predecessor_item_id: string;
  successor_item_id: string;
  link_type: ScheduleLinkType;
  lag_days: number;
};

function templateLinksAsScheduleLinks(links: ScheduleTemplateLink[]): ScheduleLink[] {
  return links.map((link) => ({
    id: link.id,
    project_id: link.template_id,
    predecessor_item_id: link.predecessor_item_id,
    successor_item_id: link.successor_item_id,
    link_type: link.link_type,
    lag_days: link.lag_days ?? 0,
    created_at: '',
  }));
}

export function validateTemplateLinkMutation(params: {
  predecessorId: string;
  successorId: string;
  links: ScheduleTemplateLink[];
  editingLinkId?: string | null;
  idToLabel?: Map<string, string>;
}): { ok: true } | { ok: false; error: string } {
  return validateScheduleLinkMutation({
    predecessorId: params.predecessorId,
    successorId: params.successorId,
    links: templateLinksAsScheduleLinks(params.links),
    editingLinkId: params.editingLinkId,
    idToLabel: params.idToLabel,
  });
}

export async function saveScheduleTemplateLinks(
  templateId: string,
  links: TemplateLinkDraft[],
  removedLinkIds: string[]
): Promise<{ error: string | null }> {
  const now = new Date().toISOString();

  if (removedLinkIds.length > 0) {
    const { error: delErr } = await supabase
      .from('jphph_schedule_template_links')
      .delete()
      .in('id', removedLinkIds);
    if (delErr) return { error: delErr.message };
  }

  for (const link of links) {
    const payload = {
      template_id: templateId,
      predecessor_item_id: link.predecessor_item_id,
      successor_item_id: link.successor_item_id,
      link_type: link.link_type,
      lag_days: link.lag_days ?? 0,
      updated_at: now,
    };

    if (link.id) {
      const { error } = await supabase
        .from('jphph_schedule_template_links')
        .update(payload)
        .eq('id', link.id);
      if (error) return { error: error.message };
    } else {
      const { error } = await supabase
        .from('jphph_schedule_template_links')
        .insert({ ...payload, created_at: now });
      if (error) return { error: error.message };
    }
  }

  await supabase
    .from('jphph_schedule_templates')
    .update({ updated_at: now })
    .eq('id', templateId);

  return { error: null };
}

export async function exportScheduleTemplateToCsv(templateId: string): Promise<{ error: string | null }> {
  const result = await fetchScheduleTemplateById(templateId);
  if (!result.template) {
    return { error: result.loadError ?? 'Template not found' };
  }

  const itemIdToSort = new Map<string, number>();
  const itemIdToTitle = new Map<string, string>();
  for (const item of result.items ?? []) {
    if (!item?.id) continue;
    itemIdToSort.set(item.id, item.sort_order ?? 0);
    itemIdToTitle.set(item.id, item.title ?? '');
  }

  const csv = buildScheduleTemplateCsv(result.items, result.links, itemIdToSort, itemIdToTitle);
  const safeName = result.template.name.replace(/[^\w\-]+/g, '_').slice(0, 60);
  downloadCsvFile(`${safeName}_schedule_template.csv`, csv);
  return { error: null };
}

/** @deprecated Use fetchScheduleTemplateById — kept for transitional imports. */
export type ScheduleTemplateTypeKey = 'development' | 'renovation';

export function templateTypeKeyToProjectType(key: ScheduleTemplateTypeKey): string {
  return key === 'development' ? 'Development' : 'Renovation';
}

export async function fetchScheduleTemplateWithItems(
  templateTypeKey: ScheduleTemplateTypeKey
): Promise<ScheduleTemplateWithItems> {
  const projectType = templateTypeKeyToProjectType(templateTypeKey);
  const templateId = await resolveDefaultTemplateId(projectType);
  if (!templateId) {
    return emptyTemplateDetail(null);
  }
  return fetchScheduleTemplateById(templateId);
}
